import { auditLogger, isValidUUID } from 'src/utils/security';
import { getSupabase } from 'src/utils/supabase';

import { profileService } from './profileService';
import { getCurrentUserId, verifyOwnership, withSecurity } from './secureServiceWrapper';

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const PDF_MIME = 'application/pdf';

export type ClientVaultDocumentType = string;

const TRANSACTIONAL_DOCUMENT_SUBTYPES = new Set([
  'Contract / Agreement',
  'Employment Agreement',
  'Service Agreement',
  'Lease / Rental Agreement',
  'Non-Disclosure Agreement (NDA)',
  'Partnership / Operating Agreement',
  'Purchase / Sales Agreement',
  'Settlement Agreement',
  'Licensing Agreement',
  'Franchise / Distribution Agreement',
  'Deed / Title / Bill of Sale',
  'Promissory Note / Financial Instrument',
  'Power of Attorney / Authorization',
]);

export function isDocumentTypeSupportingContractAnalysis(docType: string | null | undefined): boolean {
  if (!docType) return false;
  return (
    TRANSACTIONAL_DOCUMENT_SUBTYPES.has(docType) ||
    docType === 'contracts_agreements' ||
    docType === 'Transactional / Contractual Documents'
  );
}

const CONTRACT_AGREEMENT_LABEL = 'Contract / Agreement';

export function isDocumentSupportingContractAnalysis(doc: ClientVaultDocument): boolean {
  if (doc.document_type === CONTRACT_AGREEMENT_LABEL) return true;
  if (doc.document_subtype === CONTRACT_AGREEMENT_LABEL) return true;
  return isDocumentTypeSupportingContractAnalysis(doc.document_type);
}

export interface ClientVaultDocument {
  id: string;
  client_id: string;
  entity_id: string;
  file_name: string;
  file_path: string;
  file_size_bytes: number;
  document_type: ClientVaultDocumentType | null;
  summary?: Record<string, unknown> | string | null;
  friendly_name?: string | null;
  status?: string | null;
  document_subtype?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export function getDocumentDisplayName(doc: ClientVaultDocument): string {
  return doc.friendly_name ?? doc.file_name;
}

export function getSummaryDisplayText(summary: ClientVaultDocument['summary']): string | null {
  if (summary == null) return null;
  if (typeof summary === 'string') return summary;
  if (typeof summary === 'object' && summary !== null && 'text' in summary) {
    const t = (summary as Record<string, unknown>).text;
    return typeof t === 'string' ? t : t != null ? String(t) : null;
  }
  return JSON.stringify(summary);
}

export function getSummaryPreviewForCard(summary: ClientVaultDocument['summary']): string | null {
  if (summary == null) return null;
  if (typeof summary === 'string') return summary.length > 120 ? `${summary.slice(0, 117)}...` : summary;
  if (typeof summary !== 'object' || summary === null) return null;
  if ('text' in summary) {
    const t = (summary as Record<string, unknown>).text;
    const str = typeof t === 'string' ? t : t != null ? String(t) : '';
    return str.length > 120 ? `${str.slice(0, 117)}...` : str || null;
  }
  if (!isDocumentAnalysisSummary(summary)) return getSummaryDisplayText(summary);
  const s = summary as DocumentAnalysisSummary;
  const parts: string[] = [];
  const parties = Array.isArray(s.parties) ? s.parties : typeof s.parties === 'string' ? [s.parties] : [];
  if (parties.length) parts.push(`Parties: ${parties.slice(0, 3).join(', ')}${parties.length > 3 ? '...' : ''}`);
  if (s.keywords?.length)
    parts.push(`Keywords: ${s.keywords.slice(0, 5).join(', ')}${s.keywords.length > 5 ? '...' : ''}`);
  if (s.executive_summary)
    parts.push(s.executive_summary.length > 80 ? `${s.executive_summary.slice(0, 77)}...` : s.executive_summary);
  return parts.length ? parts.join(' · ') : null;
}

export const LEGAL_DOCUMENT_DISPLAY_NAMES: Record<string, string> = {
  contracts_agreements: 'Contracts & Agreements',
  'Contract / Agreement': 'Contract / Agreement',
  'Employment Agreement': 'Employment Agreement',
  'Service Agreement': 'Service Agreement',
  'Lease / Rental Agreement': 'Lease / Rental Agreement',
  'Non-Disclosure Agreement (NDA)': 'Non-Disclosure Agreement (NDA)',
  'Partnership / Operating Agreement': 'Partnership / Operating Agreement',
  'Purchase / Sales Agreement': 'Purchase / Sales Agreement',
  'Settlement Agreement': 'Settlement Agreement',
  'Licensing Agreement': 'Licensing Agreement',
  'Franchise / Distribution Agreement': 'Franchise / Distribution Agreement',
  'Deed / Title / Bill of Sale': 'Deed / Title / Bill of Sale',
  'Promissory Note / Financial Instrument': 'Promissory Note / Financial Instrument',
  'Power of Attorney / Authorization': 'Power of Attorney / Authorization',
};

export function getDocumentTypeDisplayName(docType: string | null | undefined, status?: string | null): string {
  if (!docType) {
    if (status === 'pending' || status === 'IN_PROGRESS') return 'Classifying...';
    return 'Unknown';
  }
  return LEGAL_DOCUMENT_DISPLAY_NAMES[docType] ?? docType;
}

export const VAULT_DOCUMENT_STATUS_COMPLETED = 'COMPLETED';

export interface DocumentAnalysisSummary {
  parties?: string[] | string;
  keywords?: string[];
  key_facts?: string[];
  jurisdiction?: string;
  executive_summary?: string;
}

export function isDocumentAnalysisSummary(
  summary: ClientVaultDocument['summary']
): summary is Record<string, unknown> & DocumentAnalysisSummary {
  return getDocumentAnalysisSummaryNormalized(summary) !== null;
}

export function getDocumentAnalysisSummaryNormalized(
  summary: ClientVaultDocument['summary']
): DocumentAnalysisSummary | null {
  if (summary == null) return null;
  let obj: Record<string, unknown>;
  if (typeof summary === 'string') {
    const trimmed = summary.trim();
    if (!(trimmed.startsWith('{') && trimmed.endsWith('}'))) return null;
    try {
      obj = JSON.parse(summary) as Record<string, unknown>;
    } catch {
      return null;
    }
  } else if (typeof summary === 'object' && summary !== null) {
    obj = summary as Record<string, unknown>;
  } else {
    return null;
  }
  const hasKey =
    'parties' in obj || 'keywords' in obj || 'key_facts' in obj || 'jurisdiction' in obj || 'executive_summary' in obj;
  if (!hasKey) return null;
  return obj as unknown as DocumentAnalysisSummary;
}

export function formatDocumentAnalysisSummaryAsText(summary: DocumentAnalysisSummary): string {
  const parts: string[] = [];
  const parties = Array.isArray(summary.parties)
    ? summary.parties
    : typeof summary.parties === 'string'
      ? [summary.parties]
      : [];
  if (parties.length) parts.push(`Parties: ${parties.join(', ')}`);
  if (summary.keywords?.length) parts.push(`Keywords: ${summary.keywords.join(', ')}`);
  if (summary.key_facts?.length) parts.push(`Key facts: ${summary.key_facts.join('; ')}`);
  if (summary.jurisdiction) parts.push(`Jurisdiction: ${summary.jurisdiction}`);
  if (summary.executive_summary) parts.push(summary.executive_summary);
  return parts.join('\n\n');
}

export function isVaultDocumentReadyForAnalysisDisplay(doc: ClientVaultDocument): boolean {
  return doc.status === VAULT_DOCUMENT_STATUS_COMPLETED || doc.status == null;
}

export function isVaultDocumentSelectableForContractReview(doc: ClientVaultDocument): boolean {
  if (!isDocumentSupportingContractAnalysis(doc)) return false;
  return doc.status === VAULT_DOCUMENT_STATUS_COMPLETED || doc.status == null;
}

export function getVaultDocumentStatusLabel(doc: ClientVaultDocument): string | null {
  const s = doc.status;
  if (s == null || s === VAULT_DOCUMENT_STATUS_COMPLETED) return null;
  if (s === 'pending' || s === 'IN_PROGRESS') return 'Classifying & Summarizing';
  if (s === 'FAILED') return 'Failed';
  return s;
}

export function isVaultDocumentClassifying(doc: ClientVaultDocument): boolean {
  return doc.status === 'pending' || doc.status === 'IN_PROGRESS';
}

function sanitizeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

function isPdfFile(file: File): boolean {
  if (file.type === PDF_MIME) return true;
  const name = file.name?.toLowerCase() ?? '';
  return name.endsWith('.pdf');
}

export function validateVaultFile(file: File): { valid: boolean; error?: string } {
  if (!isPdfFile(file)) {
    return { valid: false, error: 'Only PDF files are allowed.' };
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { valid: false, error: 'File size must be 50MB or less.' };
  }
  return { valid: true };
}

export const clientVaultService = {
  async listAll(): Promise<ClientVaultDocument[]> {
    return withSecurity(
      async () => {
        const userId = await getCurrentUserId();
        if (!userId) throw new Error('Not authenticated');

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('client_vault_documents')
          .select('*')
          .eq('entity_id', userId)
          .order('created_at', { ascending: false });

        if (error) {
          throw error;
        }
        return data ?? [];
      },
      { requireAuth: true, rateLimitKey: 'vault_list_all' }
    );
  },

  async countAll(): Promise<number> {
    return withSecurity(
      async () => {
        const userId = await getCurrentUserId();
        if (!userId) throw new Error('Not authenticated');

        const supabase = await getSupabase();
        const { count, error } = await supabase
          .from('client_vault_documents')
          .select('*', { count: 'exact', head: true })
          .eq('entity_id', userId);

        if (error) {
          return 0;
        }
        return count ?? 0;
      },
      { requireAuth: true, rateLimitKey: 'vault_count' }
    );
  },

  async listByClient(clientId: string, limit?: number): Promise<ClientVaultDocument[]> {
    return withSecurity(
      async () => {
        if (!isValidUUID(clientId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_client_id', service: 'clientVault' });
          throw new Error('Invalid client ID');
        }

        await verifyOwnership('clients', clientId);

        const supabase = await getSupabase();
        let query = supabase
          .from('client_vault_documents')
          .select('*')
          .eq('client_id', clientId)
          .order('created_at', { ascending: false });

        if (limit) {
          query = query.limit(limit);
        }

        const { data, error } = await query;

        if (error) {
          throw error;
        }
        return data ?? [];
      },
      { requireAuth: true, rateLimitKey: 'vault_list_by_client' }
    );
  },

  async getDocumentById(documentId: string): Promise<ClientVaultDocument | null> {
    return withSecurity(
      async () => {
        if (!isValidUUID(documentId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_document_id', service: 'clientVault' });
          throw new Error('Invalid document ID');
        }

        await verifyOwnership('client_vault_documents', documentId, 'entity_id');

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('client_vault_documents')
          .select('*')
          .eq('id', documentId)
          .maybeSingle();

        if (error || !data) {
          return null;
        }
        return data;
      },
      { requireAuth: true, rateLimitKey: 'vault_get_document' }
    );
  },

  async getDocumentsByIds(ids: string[]): Promise<ClientVaultDocument[]> {
    return withSecurity(
      async () => {
        const validIds = ids.filter(isValidUUID);
        if (validIds.length === 0) return [];

        const userId = await getCurrentUserId();
        if (!userId) throw new Error('Not authenticated');

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('client_vault_documents')
          .select('*')
          .in('id', validIds)
          .eq('entity_id', userId);

        if (error) throw error;
        return data ?? [];
      },
      { requireAuth: true, rateLimitKey: 'vault_get_documents_batch' }
    );
  },

  async upload(clientId: string, file: File): Promise<ClientVaultDocument> {
    return withSecurity(
      async () => {
        if (!isValidUUID(clientId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_client_id', service: 'clientVault' });
          throw new Error('Invalid client ID');
        }

        const validation = validateVaultFile(file);
        if (!validation.valid) {
          throw new Error(validation.error);
        }

        const profile = await profileService.getCurrentUserProfile();
        if (!profile) {
          throw new Error('User profile not found');
        }

        const supabase = await getSupabase();
        const docId = crypto.randomUUID();
        const sanitized = sanitizeFileName(file.name);
        const filePath = `${profile.id}/${clientId}/${docId}/${sanitized}`;

        const { error: uploadError } = await supabase.storage
          .from('client-vault')
          .upload(filePath, file, { contentType: PDF_MIME, upsert: false });

        if (uploadError) {
          throw new Error('Failed to upload file');
        }

        const record = {
          id: docId,
          client_id: clientId,
          entity_id: profile.id,
          file_name: file.name,
          file_path: filePath,
          file_size_bytes: file.size,
        };

        const { data, error: insertError } = await supabase
          .from('client_vault_documents')
          .insert(record)
          .select()
          .single();

        if (insertError) {
          await supabase.storage.from('client-vault').remove([filePath]);
          throw new Error('Failed to save document record');
        }

        return data;
      },
      { requireAuth: true, rateLimitKey: 'vault_upload', rateLimitMax: 20 }
    );
  },

  async getSignedUrl(documentId: string, expiresIn = 3600): Promise<string | null> {
    return withSecurity(
      async () => {
        if (!isValidUUID(documentId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_document_id', service: 'clientVault' });
          throw new Error('Invalid document ID');
        }

        const isOwner = await verifyOwnership('client_vault_documents', documentId, 'entity_id');
        if (!isOwner) {
          auditLogger.log('OWNERSHIP_VIOLATION', { service: 'clientVault', action: 'getSignedUrl' });
          throw new Error('You do not have permission to access this document');
        }

        const supabase = await getSupabase();
        const { data: doc, error: docError } = await supabase
          .from('client_vault_documents')
          .select('file_path')
          .eq('id', documentId)
          .maybeSingle();

        if (docError || !doc?.file_path) {
          return null;
        }

        const { data: signed, error: signedError } = await supabase.storage
          .from('client-vault')
          .createSignedUrl(doc.file_path, expiresIn);

        if (signedError) {
          return null;
        }
        return signed?.signedUrl ?? null;
      },
      { requireAuth: true, rateLimitKey: 'vault_signed_url', rateLimitMax: 50 }
    );
  },

  async deleteDocument(documentId: string): Promise<void> {
    return withSecurity(
      async () => {
        if (!isValidUUID(documentId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_document_id', service: 'clientVault' });
          throw new Error('Invalid document ID');
        }

        const supabase = await getSupabase();
        const { data: doc, error: fetchError } = await supabase
          .from('client_vault_documents')
          .select('id, file_path, entity_id')
          .eq('id', documentId)
          .maybeSingle();

        if (fetchError || !doc) {
          throw new Error('Document not found');
        }

        const profile = await profileService.getCurrentUserProfile();
        if (!profile || doc.entity_id !== profile.id) {
          auditLogger.log('OWNERSHIP_VIOLATION', { service: 'clientVault', action: 'delete' });
          throw new Error('You do not have permission to delete this document');
        }

        await supabase.storage.from('client-vault').remove([doc.file_path]);

        const { error: deleteRowError } = await supabase.from('client_vault_documents').delete().eq('id', documentId);

        if (deleteRowError) {
          throw deleteRowError;
        }
      },
      { requireAuth: true, rateLimitKey: 'vault_delete', rateLimitMax: 20 }
    );
  },

  async subscribeToDocumentUpdates(clientId: string, callback: (payload: unknown) => void) {
    if (!isValidUUID(clientId)) {
      auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_client_id', service: 'clientVault_subscribe' });
      return null;
    }

    const currentUserId = await getCurrentUserId();
    if (!currentUserId) {
      auditLogger.log('AUTH_FAILURE', { reason: 'no_user', service: 'clientVault_subscribe' });
      return null;
    }

    const isOwner = await verifyOwnership('clients', clientId);
    if (!isOwner) {
      auditLogger.log('OWNERSHIP_VIOLATION', { reason: 'subscribe_not_owner', service: 'clientVault_subscribe' });
      return null;
    }

    const supabase = await getSupabase();
    const channel = supabase.channel(`client_vault_documents_${clientId}_${crypto.randomUUID()}`);

    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'client_vault_documents',
        filter: `client_id=eq.${clientId}`,
      },
      callback
    );

    await channel.subscribe();
    return channel;
  },
};
